/**
 * Decides when a tab/window return should run the auth refocus flow.
 *
 * Before 04-10 the provider ran the full refocus flow (session refresh, CSRF
 * check, subscription refetch, invalidate + refetch every auth-gated query) on
 * every `focusin` — i.e. every click into a textarea or button inside the page.
 * Production 03-10: a refocus cycle every 3–5s while a new user typed, and 3–5
 * `get-subscription-status` calls per cycle.
 *
 * Now:
 *  - only real returns trigger it: `visibilitychange` to visible, window `focus`
 *    (returning from another app while the tab stayed visible) and `pageshow`;
 *    `focusin` is not a return and is not listened to;
 *  - triggers arriving together (a tab return fires visibilitychange AND focus)
 *    collapse into one run after `debounceMs`;
 *  - a run is skipped if one started less than `minIntervalMs` ago. A return
 *    after any real absence (longer than that) always runs.
 */
export type RefocusTrigger = 'visibility' | 'focus' | 'pageshow';

/** The DOM events that count as "the user came back". `focusin` is deliberately absent. */
export const REFOCUS_WINDOW_EVENTS = ['focus', 'pageshow'] as const;

export const REFOCUS_DEBOUNCE_MS = 300;
export const REFOCUS_MIN_INTERVAL_MS = 15_000;

const PRIORITY: Record<RefocusTrigger, number> = { focus: 1, pageshow: 2, visibility: 3 };

export interface RefocusSchedulerOptions {
  run: (trigger: RefocusTrigger) => void;
  /** ms timestamp of the last refocus run that actually started */
  getLastStartMs: () => number;
  onSkip?: (trigger: RefocusTrigger, sinceLastMs: number) => void;
  debounceMs?: number;
  minIntervalMs?: number;
  now?: () => number;
}

export interface RefocusScheduler {
  trigger: (trigger: RefocusTrigger) => void;
  cancel: () => void;
}

export function createRefocusScheduler(options: RefocusSchedulerOptions): RefocusScheduler {
  const debounceMs = options.debounceMs ?? REFOCUS_DEBOUNCE_MS;
  const minIntervalMs = options.minIntervalMs ?? REFOCUS_MIN_INTERVAL_MS;
  const now = options.now ?? Date.now;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending: RefocusTrigger | null = null;

  const fire = () => {
    timer = null;
    const trigger = pending;
    pending = null;
    if (!trigger) return;
    const since = now() - options.getLastStartMs();
    if (since < minIntervalMs) {
      options.onSkip?.(trigger, since);
      return;
    }
    options.run(trigger);
  };

  return {
    trigger(trigger) {
      if (!pending || PRIORITY[trigger] > PRIORITY[pending]) pending = trigger;
      if (timer) clearTimeout(timer);
      timer = setTimeout(fire, debounceMs);
    },
    cancel() {
      if (timer) clearTimeout(timer);
      timer = null;
      pending = null;
    },
  };
}
